import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { LogoMark } from "@/components/app/Brand";
import BookDemoModal from "@/components/app/BookDemoModal";
import {
  LayoutGrid, Sparkles, AlertTriangle, Columns3, BarChart3,
  Receipt, CheckCircle2, ArrowRight, Menu, X,
  Zap, Users, TrendingUp, Star, ChevronRight,
  Calendar, MessageSquare, FolderOpen, Target, Loader2,
  Activity, ClipboardList, XCircle,
} from "lucide-react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { useTheme } from "@/lib/theme-context";

// ─── ENTERPRISE MODAL ────────────────────────────────────────────────────────

const EnterpriseModal = ({ onClose }) => {
  const [form, setForm] = useState({ name: "", email: "", company: "", employees: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) { setError("Please enter your name"); return; }
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) { setError("Please enter a valid email"); return; }
    if (!form.company.trim()) { setError("Please enter your company name"); return; }
    setError(""); setBusy(true);
    try {
      await api.post("/demo-bookings", {
        name: form.name, email: form.email, company: form.company,
        company_size: form.employees, message: form.message, role: "Enterprise enquiry", preferred_time: "",
      });
      setDone(true);
    } catch { setError("Something went wrong — please try again"); }
    finally { setBusy(false); }
  };

  const modal = (
    <div style={{ position: "fixed", inset: 0, zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }} />
      <div style={{ position: "relative", width: "100%", maxWidth: 480, background: "#fff", borderRadius: 20, boxShadow: "0 25px 60px rgba(0,0,0,0.15)", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ height: 4, background: "linear-gradient(90deg,#4F46E5,#818BF4,#4F46E5)", borderRadius: "20px 20px 0 0" }} />
        <button onClick={onClose} style={{ position: "absolute", top: 16, right: 16, background: "#F3F4F6", border: "none", cursor: "pointer", color: "#6B7280", borderRadius: "50%", width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <X style={{ width: 16, height: 16 }} />
        </button>
        {done ? (
          <div style={{ padding: "3rem 2rem", textAlign: "center" }}>
            <CheckCircle2 style={{ width: 48, height: 48, color: "#4F46E5", margin: "0 auto 16px" }} />
            <h2 style={{ fontSize: 22, fontWeight: 800, color: "#111827", marginBottom: 8 }}>Enquiry received!</h2>
            <p style={{ color: "#6B7280", marginBottom: 24 }}>Our enterprise team will be in touch within one business day.</p>
            <button onClick={onClose} style={{ background: "#4F46E5", color: "#fff", fontWeight: 700, padding: "10px 32px", border: "none", cursor: "pointer", borderRadius: 9999 }}>Close</button>
          </div>
        ) : (
          <div style={{ padding: "2rem" }}>
            <div style={{ marginBottom: "1.5rem" }}>
              <span style={{ display: "inline-block", padding: "4px 12px", background: "#F5F3FF", border: "1px solid #DDD6FE", borderRadius: 999, color: "#4F46E5", fontSize: 11, fontWeight: 600, marginBottom: 12 }}>Enterprise</span>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: "#111827", margin: "0 0 4px 0" }}>Let's talk</h2>
              <p style={{ color: "#6B7280", fontSize: 13, margin: 0 }}>Tell us about your team and we'll build a custom plan.</p>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                {[["name","Your name *","Jane Smith"],["email","Work email *","jane@company.com"]].map(([k,lbl,ph]) => (
                  <div key={k}>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#374151", marginBottom: 4 }}>{lbl}</label>
                    <input value={form[k]} onChange={e => set(k, e.target.value)} placeholder={ph}
                      style={{ width: "100%", height: 40, background: "#F9FAFB", border: "1.5px solid #E5E7EB", borderRadius: 10, color: "#111827", fontSize: 13, padding: "0 12px", outline: "none", boxSizing: "border-box" }} />
                  </div>
                ))}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                {[["company","Company *","Acme Ltd"],["employees","No. of employees","e.g. 200"]].map(([k,lbl,ph]) => (
                  <div key={k}>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#374151", marginBottom: 4 }}>{lbl}</label>
                    <input value={form[k]} onChange={e => set(k, e.target.value)} placeholder={ph}
                      style={{ width: "100%", height: 40, background: "#F9FAFB", border: "1.5px solid #E5E7EB", borderRadius: 10, color: "#111827", fontSize: 13, padding: "0 12px", outline: "none", boxSizing: "border-box" }} />
                  </div>
                ))}
              </div>
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#374151", marginBottom: 4 }}>What are you looking to solve? (optional)</label>
                <textarea value={form.message} onChange={e => set("message", e.target.value)} rows={3}
                  placeholder="e.g. We need SSO, custom data migration and a dedicated success manager…"
                  style={{ width: "100%", background: "#F9FAFB", border: "1.5px solid #E5E7EB", borderRadius: 10, color: "#111827", fontSize: 13, padding: "8px 12px", outline: "none", resize: "none", boxSizing: "border-box" }} />
              </div>
              {error && <p style={{ color: "#EF4444", fontSize: 12, margin: 0 }}>{error}</p>}
              <button onClick={submit} disabled={busy}
                style={{ width: "100%", height: 48, background: busy ? "#818BF4" : "#4F46E5", color: "#fff", fontWeight: 700, fontSize: 14, border: "none", cursor: busy ? "not-allowed" : "pointer", borderRadius: 9999, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                {busy ? <Loader2 style={{ width: 16, height: 16 }} className="animate-spin" /> : <>Send enquiry <ArrowRight style={{ width: 16, height: 16 }} /></>}
              </button>
              <p style={{ textAlign: "center", fontSize: 11, color: "#9CA3AF", margin: 0 }}>We'll respond within one business day.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
  return createPortal(modal, document.body);
};

// ─── DATA ────────────────────────────────────────────────────────────────────

const PLANS = [
  {
    key: "free", label: "Starter", price: "Free", sub: "forever",
    limits: "1 project · 1 member", cta: "Get started free", href: "/checkout?plan=starter",
    highlight: false, badge: null,
    features: ["1 active project", "1 team member", "Kanban board", "Project wiki", "Community support"],
  },
  {
    key: "pro", label: "Pro", price: "£9.99", sub: "per user / month",
    limits: "Up to 3 projects · 10 seats", cta: "Start 7-day free trial", href: "/checkout?plan=pro",
    highlight: false, badge: null,
    features: ["Everything in Starter", "Up to 3 projects", "Up to 10 seats (£9.99 per seat/mo)", "Gantt timeline & sprints", "AI project assistant", "Time & expense tracking", "Deals CRM pipeline", "Email support"],
  },
  {
    key: "business", label: "Business", price: "£24.99", sub: "per seat / month",
    limits: "Up to 30 seats · unlimited projects", cta: "Start 7-day free trial", href: "/checkout?plan=business",
    highlight: true, badge: "Most popular",
    features: ["Everything in Starter & Pro", "Up to 30 seats (£24.99 per seat/mo)", "Unlimited projects", "Budget tracking & burndown", "Xero & HubSpot integrations", "Automated milestone invoicing", "Compliance document vault", "ML margin forecasting", "Priority support"],
  },
  {
    key: "enterprise", label: "Enterprise", price: "Custom", sub: "contact us",
    limits: "Custom seats, storage & SLA", cta: "Contact sales", href: "mailto:sales@combinedwork.com?subject=Enterprise%20enquiry",
    highlight: false, badge: null,
    features: ["Everything in Business", "Single Sign-On (SSO)", "Custom data migration", "Dedicated success manager", "Custom SLA & uptime guarantee", "White-glove onboarding"],
  },
];

const FEATURES = [
  { icon: Activity,      name: "See project health instantly", desc: "Get a clear RAG view of what's on track, what needs attention and where action is required.", color: "#10B981", bg: "#ECFDF5" },
  { icon: BarChart3,     name: "Keep reporting connected to delivery", desc: "Turn live project information into clearer updates without rebuilding reports every week.", color: "#4F46E5", bg: "#F5F3FF" },
  { icon: Users,         name: "Know who's doing what", desc: "Manage stakeholders, responsibilities and resources alongside the work itself.", color: "#0EA5E9", bg: "#F0F9FF" },
  { icon: AlertTriangle, name: "Spot risks before they become problems", desc: "Keep risks, issues, actions and dependencies visible and connected to your projects.", color: "#F59E0B", bg: "#FFFBEB" },
  { icon: ClipboardList, name: "Build governance into the way you work", desc: "Keep the controls, information and oversight you need without adding unnecessary admin.", color: "#6366F1", bg: "#EEF2FF" },
  { icon: Sparkles,      name: "Get smarter project updates", desc: "Use AI-powered features to help turn project information into useful updates and insights.", color: "#EC4899", bg: "#FDF2F8" },
];

const PAIN_POINTS = [
  { icon: FolderOpen,     text: "Your project plan lives in one tool." },
  { icon: AlertTriangle,  text: "Risks are sitting in a spreadsheet." },
  { icon: Users,          text: "Stakeholder information is somewhere else." },
  { icon: MessageSquare,  text: "Updates are buried in emails and Teams messages." },
];

const WITHOUT_WITH = [
  { without: "Multiple spreadsheets",           withIt: "One project workspace" },
  { without: "Disconnected tools",              withIt: "Clear project health" },
  { without: "Manual status updates",           withIt: "Connected risks and actions" },
  { without: "Scattered project information",   withIt: "Better stakeholder visibility" },
  { without: "Limited visibility",              withIt: "Simpler reporting" },
  { without: "Hours spent preparing reports",   withIt: "A clearer picture of delivery" },
];

const AI_CANNED_RESPONSES = [
  {
    match: /status|report/i,
    reply: "🟢 Overall Health: On Track — Website Redesign is 78% complete. Completed: design system, stakeholder sign-off. Next: content migration, UAT. One open risk: CMS vendor delivery.",
  },
  {
    match: /risk|raid/i,
    reply: "⚠️ 2 open risks on Website Redesign: CMS vendor delivery delay (High) and stakeholder availability (Medium) — both logged in the RAID register with mitigation owners assigned.",
  },
  {
    match: /budget|margin|forecast|cost/i,
    reply: "📉 AI Forecast: Website Redesign is trending to a 24% final margin — 6 points below your 30% target. Predictive Intelligence flagged this as Moderate risk 2 days ago.",
  },
  {
    match: /invoice|bill|payment/i,
    reply: "🧾 3 invoices outstanding: INV-0041 (£12,800, overdue), INV-0040 (£7,250, sent). Want me to draft a payment reminder for the overdue one?",
  },
  {
    match: /capacity|team|resourc/i,
    reply: "👥 The design team is at 118% capacity next sprint — 2 members are double-booked across Website Redesign and Mobile App v2.0.",
  },
];
const AI_FALLBACK_REPLY = "I can help with project status, RAID risks, margin forecasts, invoices, and team capacity — try one of the prompts below, or ask about a specific project.";

function AiAssistantDemo() {
  const [messages, setMessages] = useState([
    { role: "ai", text: "Hi! Ask me about a project's status, risks, or budget — try one of the prompts below." },
  ]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [messages, thinking]);

  const send = (text) => {
    const q = (text ?? input).trim();
    if (!q || thinking) return;
    setMessages((m) => [...m, { role: "user", text: q }]);
    setInput("");
    setThinking(true);
    setTimeout(() => {
      const found = AI_CANNED_RESPONSES.find((r) => r.match.test(q));
      setMessages((m) => [...m, { role: "ai", text: found ? found.reply : AI_FALLBACK_REPLY }]);
      setThinking(false);
    }, 700);
  };

  return (
    <div className="p-4 bg-white h-full rounded-b-2xl flex flex-col">
      <div className="text-xs font-bold text-gray-800 mb-2 flex items-center gap-1.5">
        <Sparkles className="w-3.5 h-3.5 text-violet-500" /> AI Assistant
        <span className="ml-auto text-[9px] font-normal text-gray-400">Try it — type a question</span>
      </div>
      <div className="flex-1 overflow-y-auto space-y-2 mb-2 pr-1" style={{ maxHeight: 170 }}>
        {messages.map((m, i) => (
          <div key={i}
            className={m.role === "user"
              ? "bg-gray-50 rounded-xl p-2.5 text-[10px] text-gray-600 ml-6"
              : "bg-violet-50 border border-violet-100 rounded-xl p-2.5 text-[10px] text-gray-600 leading-relaxed mr-6"}>
            <span className={m.role === "user" ? "text-gray-500 font-semibold" : "text-violet-600 font-semibold"}>
              {m.role === "user" ? "You: " : "AI: "}
            </span>
            {m.text}
          </div>
        ))}
        {thinking && (
          <div className="bg-violet-50 border border-violet-100 rounded-xl p-2.5 text-[10px] text-violet-400 mr-6">
            AI is thinking…
          </div>
        )}
        <div ref={endRef} />
      </div>
      <div className="flex gap-1.5 mb-2 flex-wrap">
        {["Status report", "What's at risk?", "Forecast margin"].map((p) => (
          <button key={p} type="button" onClick={() => send(p)}
            className="px-2 py-1 bg-gray-100 hover:bg-violet-100 hover:text-violet-700 border border-gray-200 rounded-full text-[9px] text-gray-500 transition-colors">
            {p}
          </button>
        ))}
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-1.5">
        <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask about a project…"
          className="flex-1 h-8 bg-gray-50 border border-gray-200 rounded-full px-3 text-[10px] outline-none focus:border-violet-300" />
        <button type="submit" className="w-8 h-8 grid place-items-center bg-violet-600 hover:bg-violet-700 text-white rounded-full shrink-0">
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </form>
    </div>
  );
}

const TABS = [
  {
    id: "dashboard", label: "Dashboard", icon: LayoutGrid,
    headline: "Everything in one view",
    sub: "Live portfolio health, outstanding invoices, and team workload — all on your morning dashboard.",
    preview: (
      <div className="p-5 bg-white h-full rounded-b-2xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] text-violet-500 font-semibold mb-0.5">Dashboard</div>
            <div className="text-sm font-bold text-gray-800">Good morning, Alex 👋</div>
          </div>
          <div className="px-3 py-1.5 bg-violet-600 text-white text-[10px] font-bold rounded-full">+ New project</div>
        </div>
        <div className="grid grid-cols-3 gap-2 mb-4">
          {[["£48.2k","Revenue","#4F46E5"],["3","At risk","#F59E0B"],["94%","On track","#10B981"]].map(([v,l,c]) => (
            <div key={l} className="bg-gray-50 border border-gray-100 rounded-xl p-2.5">
              <div className="text-[9px] text-gray-400 mb-0.5">{l}</div>
              <div className="text-lg font-bold font-mono" style={{color:c}}>{v}</div>
            </div>
          ))}
        </div>
        <div className="border border-gray-100 rounded-xl overflow-hidden">
          {[["Website Redesign",78,"#4F46E5"],["Mobile App v2.0",43,"#F59E0B"],["Data Migration",94,"#10B981"]].map(([n,p,c]) => (
            <div key={n} className="flex items-center gap-2 px-3 py-2 border-b border-gray-50 last:border-0">
              <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{background:c}} />
              <span className="text-xs text-gray-700 flex-1">{n}</span>
              <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full" style={{width:`${p}%`,background:c}} />
              </div>
              <span className="text-[10px] text-gray-400 w-7 text-right font-mono">{p}%</span>
            </div>
          ))}
        </div>
      </div>
    ),
  },
  {
    id: "kanban", label: "Kanban Board", icon: Columns3,
    headline: "Agile boards that actually work",
    sub: "Drag tasks between columns, assign owners, set due dates, and watch your sprint burn down in real time.",
    preview: (
      <div className="p-4 bg-white h-full rounded-b-2xl">
        <div className="text-xs font-bold text-gray-800 mb-3">Sprint 4 — Website Redesign</div>
        <div className="grid grid-cols-3 gap-2 h-[calc(100%-2rem)]">
          {[
            { col: "To Do", color: "#94A3B8", tasks: ["Write homepage copy", "Setup CI/CD pipeline"] },
            { col: "In Progress", color: "#F59E0B", tasks: ["Design system tokens", "API auth endpoints"] },
            { col: "Done", color: "#10B981", tasks: ["Project kickoff", "Stakeholder mapping", "Tech stack decision"] },
          ].map(({col, color, tasks}) => (
            <div key={col} className="bg-gray-50 border border-gray-100 rounded-xl p-2 flex flex-col gap-1.5">
              <div className="flex items-center gap-1.5 mb-1">
                <div className="w-2 h-2 rounded-full" style={{background:color}} />
                <span className="text-[9px] font-semibold text-gray-500 uppercase tracking-wider">{col}</span>
                <span className="ml-auto text-[9px] text-gray-300">{tasks.length}</span>
              </div>
              {tasks.map(t => (
                <div key={t} className="bg-white border border-gray-100 rounded-lg p-1.5 text-[9px] text-gray-700 leading-tight shadow-sm">{t}</div>
              ))}
            </div>
          ))}
        </div>
      </div>
    ),
  },
  {
    id: "invoicing", label: "Invoicing", icon: Receipt,
    headline: "Invoice from milestone to payment",
    sub: "Auto-generate invoices when milestones close. Sync to Xero or QuickBooks. Chase overdue payments automatically.",
    preview: (
      <div className="p-4 bg-white h-full rounded-b-2xl">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs font-bold text-gray-800">Invoices</div>
          <div className="px-2 py-1 bg-violet-600 text-white text-[9px] font-bold rounded-full">+ New invoice</div>
        </div>
        {[
          { id:"INV-0042", client:"Acme Corp", amount:"£4,500", status:"Paid", color:"#10B981" },
          { id:"INV-0041", client:"BuildRight Ltd", amount:"£12,800", status:"Overdue", color:"#EF4444" },
          { id:"INV-0040", client:"NovaTech", amount:"£7,250", status:"Sent", color:"#F59E0B" },
          { id:"INV-0039", client:"Acme Corp", amount:"£3,100", status:"Draft", color:"#94A3B8" },
        ].map(inv => (
          <div key={inv.id} className="flex items-center gap-2 py-2 border-b border-gray-50 last:border-0">
            <div>
              <div className="text-[10px] font-semibold text-gray-800">{inv.id}</div>
              <div className="text-[9px] text-gray-400">{inv.client}</div>
            </div>
            <div className="ml-auto text-[10px] font-mono font-semibold text-gray-700">{inv.amount}</div>
            <div className="text-[8px] font-semibold px-1.5 py-0.5 rounded-full" style={{color:inv.color,background:`${inv.color}20`}}>{inv.status}</div>
          </div>
        ))}
      </div>
    ),
  },
  {
    id: "ai", label: "AI Assistant", icon: Sparkles,
    headline: "Your AI project manager",
    sub: "Generate project charters, RAID logs, status reports, and meeting notes from a single prompt.",
    preview: <AiAssistantDemo />,
  },
];

const FAQS = [
  { q: "Is there a free plan?", a: "Yes — Starter is free forever for 1 project and 1 team member, no credit card required. Every paid plan also comes with a 7-day free trial." },
  { q: "Can I cancel anytime?", a: "Yes. All paid plans are month-to-month (or annual with a discount), with no lock-in — cancel anytime from your Billing settings." },
  { q: "Do you integrate with Xero, HubSpot, and Jira?", a: "Yes — Xero and HubSpot sync natively, and Jira issues map to CombinedWork tasks via inbound webhooks. 13+ integrations in total, with more added regularly." },
  { q: "How does the AI margin forecasting work?", a: "It runs a regression across your project's weekly cost and progress data to project the final margin at completion, then flags projects trending below your target so PMs can act before it becomes a problem." },
  { q: "Is my data secure?", a: "Every tenant is fully isolated and passwords are bcrypt-hashed. Business and Enterprise plans add SSO, SCIM provisioning, and a compliance document vault with cryptographic sign-off chains." },
  { q: "Can I switch plans later?", a: "Yes — upgrade or downgrade at any time from the Billing page. Changes are prorated automatically." },
];

function FaqItem({ q, a, isOpen, onToggle }) {
  return (
    <div className="border border-gray-100 rounded-2xl overflow-hidden bg-white">
      <button type="button" onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-gray-50 transition-colors">
        <span className="text-sm font-semibold text-gray-800">{q}</span>
        <ChevronRight className={`w-4 h-4 text-violet-400 shrink-0 transition-transform duration-300 ${isOpen ? "rotate-90" : ""}`} />
      </button>
      <div className={`grid transition-all duration-300 ease-in-out ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
        <div className="overflow-hidden">
          <p className="px-5 pb-4 text-sm text-gray-500 leading-relaxed">{a}</p>
        </div>
      </div>
    </div>
  );
}

const TESTIMONIALS = [
  { name: "Miriam Hall", role: "Owner, Mermaid Island", text: "An outstanding platform — it's given us complete visibility over every project we run. Couldn't recommend it more.", stars: 5 },
  { name: "M. Panthon", role: "Klean Services", text: "The CRM deal pipeline has transformed how we manage our sales pipeline. Simple to use and keeps the whole team focused.", stars: 5 },
  { name: "Ashir Milla", role: "Scooterbuzz", text: "Project management has never been easier. CombinedWork keeps our whole team aligned and on schedule.", stars: 5 },
  { name: "Nathan Brown", role: "Waterbrowns Driving", text: "Managing our current projects and staff used to mean juggling spreadsheets — now it's all in one place and effortless.", stars: 5 },
];

// ─── UTILS ───────────────────────────────────────────────────────────────────

function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function Reveal({ children, delay = 0, className = "" }) {
  const [ref, visible] = useInView();
  return (
    <div ref={ref} style={{ transitionDelay: `${delay}ms` }}
      className={`transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"} ${className}`}>
      {children}
    </div>
  );
}

function AnimatedNumber({ target, prefix = "", suffix = "", duration = 1800 }) {
  const [val, setVal] = useState(0);
  const [ref, visible] = useInView(0.3);
  useEffect(() => {
    if (!visible) return;
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setVal(Math.round(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [visible, target, duration]);
  return <span ref={ref}>{prefix}{val.toLocaleString()}{suffix}</span>;
}

// ─── LANDING ─────────────────────────────────────────────────────────────────

const Landing = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [billingAnnual, setBillingAnnual] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);
  const [enterpriseOpen, setEnterpriseOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const { theme } = useTheme();

  // The in-app dashboard theme toggle stamps data-theme="light" on <html> by
  // default, which remaps .text-white (and friends) to a dark navy for the
  // dashboard's own dark-mode palette. This public marketing page always
  // renders its own fixed light theme with a few deliberately dark sections
  // (footer, bottom CTA) — those .text-white overrides make that text
  // unreadable. Landing doesn't use any of the dashboard's hardcoded brand
  // hex classes that the dark-theme table remaps, so switching to
  // data-theme="dark" here is a safe no-op for everything Landing renders.
  //
  // ThemeProvider wraps this whole app and re-asserts its own data-theme
  // value in its own effect on every mount/theme change. Since it's an
  // ancestor, its effect fires *after* this one on initial mount and
  // silently clobbers the override back to "light" — a plain one-shot
  // setAttribute isn't enough. Watch the attribute for as long as Landing
  // is mounted and re-force it every time something else changes it.
  //
  // On unmount, restore ThemeProvider's actual `theme` state rather than
  // whatever `data-theme` happened to read off the DOM at mount time — on a
  // fresh page load Landing's effect can fire before ThemeProvider's own
  // mount effect has committed, so that DOM read was often null, which then
  // *removed* the data-theme attribute entirely on navigating away and left
  // every other page (Pricing, Checkout, …) with no theme applied.
  useEffect(() => {
    const html = document.documentElement;
    const force = () => {
      if (html.getAttribute("data-theme") !== "dark") html.setAttribute("data-theme", "dark");
    };
    force();
    const observer = new MutationObserver(force);
    observer.observe(html, { attributes: true, attributeFilter: ["data-theme"] });
    return () => {
      observer.disconnect();
      html.setAttribute("data-theme", theme);
    };
  }, [theme]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const [tabsHovered, setTabsHovered] = useState(false);
  const [tabsClickPaused, setTabsClickPaused] = useState(false);
  const resumeTabsRef = useRef(null);

  const selectTab = useCallback((i) => {
    setActiveTab(i);
    setTabsClickPaused(true);
    if (resumeTabsRef.current) clearTimeout(resumeTabsRef.current);
    resumeTabsRef.current = setTimeout(() => setTabsClickPaused(false), 8000);
  }, []);

  useEffect(() => () => { if (resumeTabsRef.current) clearTimeout(resumeTabsRef.current); }, []);

  useEffect(() => {
    if (tabsHovered || tabsClickPaused) return;
    const t = setInterval(() => setActiveTab(v => (v + 1) % TABS.length), 4000);
    return () => clearInterval(t);
  }, [tabsHovered, tabsClickPaused]);

  const annualPrice = (p) => {
    if (p === "Custom" || p === "Free") return p;
    const n = parseFloat(p.replace("£", ""));
    const perMonth = Math.round((n * 10 / 12) * 100) / 100;
    return `£${perMonth % 1 === 0 ? perMonth : perMonth.toFixed(2)}`;
  };

  return (
    <div className="bg-white text-gray-900 min-h-screen font-sans">

      {/* ── NAV ──────────────────────────────────────────────────────────── */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 ${
        scrolled ? "bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm" : "bg-white/90 backdrop-blur-sm"
      }`}>
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-violet-600 grid place-items-center shadow-md shadow-violet-200">
              <LogoMark size={16} color="#fff" />
            </div>
            <span className="text-base font-bold text-gray-900 tracking-tight">Combined<span className="text-violet-600">Work</span></span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {[["Features","#features"],["How it works","#how"],["Pricing","#pricing"],["FAQ","#faq"]].map(([label, href]) => (
              <a key={label} href={href} className="text-sm text-gray-500 hover:text-gray-900 transition-colors font-medium">{label}</a>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <Link to="/auth" data-testid="nav-signin" className="text-sm text-gray-500 hover:text-gray-900 transition-colors font-medium">Sign in</Link>
            <button onClick={() => setDemoOpen(true)} className="px-4 py-2 border border-gray-200 text-gray-600 text-sm font-medium rounded-full hover:border-gray-300 hover:text-gray-900 transition-colors">
              Book a demo
            </button>
            <Link to="/pricing" data-testid="nav-cta" className="px-4 py-2 bg-violet-600 text-white text-sm font-bold rounded-full hover:bg-violet-700 transition-colors shadow-md shadow-violet-200">
              Start for free
            </Link>
          </div>

          <button onClick={() => setMobileOpen(v => !v)} className="md:hidden p-2 text-gray-500 hover:text-gray-900">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4 space-y-3">
            {[["Features","#features"],["How it works","#how"],["Pricing","#pricing"],["FAQ","#faq"]].map(([label, href]) => (
              <a key={label} href={href} onClick={() => setMobileOpen(false)}
                className="block text-sm text-gray-600 hover:text-gray-900 py-2 font-medium">{label}</a>
            ))}
            <div className="pt-3 border-t border-gray-100 flex flex-col gap-2">
              <Link to="/auth" className="text-sm text-gray-600 hover:text-gray-900 py-2 font-medium">Sign in</Link>
              <button onClick={() => { setMobileOpen(false); setDemoOpen(true); }}
                className="px-4 py-2.5 border border-gray-200 text-gray-700 text-sm font-semibold rounded-full text-center hover:border-violet-300">
                Book a demo
              </button>
              <Link to="/pricing" className="px-4 py-2.5 bg-violet-600 text-white text-sm font-bold rounded-full text-center hover:bg-violet-700">
                Start for free
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden bg-gradient-to-b from-violet-50 via-white to-white">
        {/* Decorative blobs */}
        <div className="absolute top-10 left-1/4 w-96 h-96 bg-violet-200 rounded-full blur-3xl opacity-30 pointer-events-none" />
        <div className="absolute top-20 right-1/4 w-72 h-72 bg-pink-200 rounded-full blur-3xl opacity-20 pointer-events-none" />

        <div className="relative max-w-6xl mx-auto">
          <div className="flex justify-center mb-8">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-violet-100 rounded-full text-xs font-semibold text-violet-600 shadow-sm shadow-violet-100">
              <span className="w-1.5 h-1.5 rounded-full bg-violet-500 animate-pulse" />
              Now in public beta · Free plan available
            </span>
          </div>

          <h1 data-testid="hero-headline" className="text-center font-display font-bold text-[clamp(2.25rem,5.5vw,4.5rem)] tracking-tight text-gray-900 leading-[1.1] max-w-4xl mx-auto mb-6">
            Know which projects<br className="hidden md:block" />
            <span className="text-violet-600"> will miss margin — before they do</span>
          </h1>

          <p className="text-center text-lg text-gray-500 max-w-2xl mx-auto mb-4 leading-relaxed">
            CombinedWork is the early-warning system for fixed-fee agencies. It reads your live HubSpot and Xero data and flags projects drifting toward margin breach while there's still time to act.
          </p>

          <p className="text-center text-base text-gray-500 max-w-2xl mx-auto mb-10 leading-relaxed">
            Plus the full delivery toolkit underneath — projects, governance, resourcing, risk and reporting in one place — so once margin risk is flagged, you can act on it without switching tools.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-5">
            <Link to="/pricing" data-testid="hero-cta"
              className="px-8 py-3.5 bg-violet-600 text-white text-base font-bold rounded-full hover:bg-violet-700 transition-all hover:scale-105 flex items-center gap-2 shadow-lg shadow-violet-200">
              Start for free <ArrowRight className="w-4 h-4" />
            </Link>
            <button onClick={() => setDemoOpen(true)} data-testid="hero-book-demo"
              className="px-8 py-3.5 border border-gray-200 text-gray-700 text-base font-semibold rounded-full hover:border-violet-200 hover:text-violet-700 transition-colors flex items-center gap-2 bg-white shadow-sm">
              <Calendar className="w-4 h-4 text-violet-500" /> Book a demo
            </button>
          </div>
          <p className="text-center text-xs text-gray-400 mb-14">No complicated setup. Get your team up and running quickly.</p>

          {/* Interactive feature tabs */}
          <Reveal>
            <div onMouseEnter={() => setTabsHovered(true)} onMouseLeave={() => setTabsHovered(false)}
              className="relative rounded-2xl overflow-hidden border border-gray-200 shadow-2xl shadow-gray-200/80 max-w-5xl mx-auto select-none bg-white">
              {/* Titlebar */}
              <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-100">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-yellow-400" />
                  <div className="w-3 h-3 rounded-full bg-green-400" />
                </div>
                <div className="flex gap-1 ml-4 overflow-x-auto">
                  {TABS.map((tab, i) => (
                    <button key={tab.id} onClick={() => selectTab(i)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                        activeTab === i
                          ? "bg-violet-600 text-white shadow-sm"
                          : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                      }`}>
                      <tab.icon className="w-3 h-3" />
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex">
                {/* Sidebar */}
                <div className="w-40 border-r border-gray-100 p-3 space-y-1 hidden sm:block bg-gray-50">
                  {["Dashboard","My Tasks","Workspace","Invoices","Team","Reports"].map((item, i) => (
                    <div key={item} className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs ${
                      i === 0 ? "bg-violet-100 text-violet-700 font-medium" : "text-gray-400"
                    }`}>
                      <div className={`w-3 h-3 rounded-md ${i === 0 ? "bg-violet-300" : "bg-gray-200"}`} />
                      {item}
                    </div>
                  ))}
                </div>

                {/* Preview pane */}
                <div className="flex-1 min-h-[280px] transition-all duration-300">
                  {TABS[activeTab].preview}
                </div>
              </div>

              {/* Tab progress bar */}
              <div className="h-1 bg-gray-100">
                <div className="h-full bg-violet-600 transition-all duration-300 rounded-full"
                  style={{ width: `${((activeTab + 1) / TABS.length) * 100}%` }} />
              </div>
            </div>

            {/* Caption */}
            <div className="text-center mt-5">
              <p className="text-sm font-semibold text-gray-800">{TABS[activeTab].headline}</p>
              <p className="text-xs text-gray-400 mt-1 max-w-md mx-auto">{TABS[activeTab].sub}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── SOUND FAMILIAR (PAIN POINTS) ────────────────────────────────── */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <Reveal>
            <h2 className="font-display font-bold text-3xl lg:text-4xl tracking-tight text-gray-900 mb-10">
              Sound familiar?
            </h2>
          </Reveal>
          <div className="space-y-4 mb-12 max-w-xl mx-auto text-left">
            {PAIN_POINTS.map((p, i) => (
              <Reveal key={p.text} delay={i * 70}>
                <div className="flex items-center gap-4 bg-gray-50 border border-gray-100 rounded-2xl px-5 py-4">
                  <div className="w-9 h-9 rounded-xl bg-white border border-gray-200 grid place-items-center shrink-0">
                    <p.icon className="w-4 h-4 text-gray-400" />
                  </div>
                  <span className="text-gray-700 text-sm sm:text-base font-medium">{p.text}</span>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <p className="text-gray-400 text-sm mb-3">And when someone asks:</p>
            <div className="inline-block bg-violet-50 border border-violet-100 rounded-2xl rounded-bl-sm px-6 py-4 mb-3">
              <p className="text-violet-700 font-semibold text-lg italic">"Where are we with this project?"</p>
            </div>
            <p className="text-gray-500 text-sm mb-10">…someone has to spend half the day finding out.</p>
            <p className="font-display font-bold text-xl sm:text-2xl text-gray-900">
              That's the problem CombinedWork was built to solve.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── LOGOS / TRUST STRIP ──────────────────────────────────────────── */}
      <div className="border-y border-gray-100 py-6 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-center gap-8 opacity-50">
          {["Xero","HubSpot","Slack","QuickBooks","DocuSign","Google Workspace","Monday.com"].map(b => (
            <span key={b} className="text-xs font-semibold text-gray-400 tracking-wide uppercase">{b}</span>
          ))}
        </div>
        <p className="text-center text-[10px] text-gray-400 mt-3">Integrates with the tools your team already uses</p>
      </div>

      {/* ── STATS ────────────────────────────────────────────────────────── */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-5xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {[
            { n: 10, suffix: "×", label: "Faster documentation with AI" },
            { n: 13, suffix: "+", label: "Integrations out of the box" },
            { n: 100, suffix: "%", label: "Of your project lifecycle covered" },
            { n: 0, prefix: "£", suffix: "", label: "To get started today" },
          ].map(({ n, prefix="", suffix, label }) => (
            <Reveal key={label}>
              <div className="text-center">
                <div className="font-display font-bold text-4xl text-violet-600 mb-1">
                  <AnimatedNumber target={n} prefix={prefix} suffix={suffix} />
                </div>
                <div className="text-sm text-gray-500 leading-snug">{label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ── FEATURES ─────────────────────────────────────────────────────── */}
      <section id="features" className="py-24 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <Reveal className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-violet-100 text-violet-600 text-xs font-semibold rounded-full mb-4">The solution</span>
            <h2 className="font-display font-bold text-3xl lg:text-4xl tracking-tight text-gray-900 mb-4">
              One place to actually run your projects.
            </h2>
            <p className="text-gray-500 text-base max-w-xl mx-auto">
              CombinedWork gives your team a single view of project delivery — without forcing you to stitch together multiple systems.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
            {FEATURES.map((f, i) => (
              <Reveal key={f.name} delay={i * 50}>
                <div className="bg-white rounded-2xl p-6 hover:shadow-lg hover:shadow-gray-100 transition-all hover:-translate-y-1 group cursor-default border border-gray-100">
                  <div className="w-10 h-10 rounded-xl grid place-items-center mb-4 transition-all" style={{background: f.bg}}>
                    <f.icon className="w-5 h-5" style={{color: f.color}} strokeWidth={1.75} />
                  </div>
                  <h3 className="font-semibold text-gray-800 text-sm mb-2">{f.name}</h3>
                  <p className="text-gray-400 text-xs leading-relaxed">{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="text-center">
            <Link to="/pricing"
              className="inline-flex items-center gap-2 px-6 py-3 bg-violet-600 text-white text-sm font-bold rounded-full hover:bg-violet-700 transition-all hover:scale-105 shadow-md shadow-violet-200">
              Try all features free for 7 days <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ── HOW IT WORKS ─────────────────────────────────────────────────── */}
      <section id="how" className="py-24 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <Reveal className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-violet-100 text-violet-600 text-xs font-semibold rounded-full mb-4">Getting started</span>
            <h2 className="font-display font-bold text-3xl lg:text-4xl tracking-tight text-gray-900 mb-4">
              Up and running in minutes
            </h2>
            <p className="text-gray-500 text-base max-w-xl mx-auto">
              No lengthy onboarding. No consultant required. Just sign up and start delivering.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
            {[
              { step:"01", icon: Users, title:"Create your workspace", desc:"Start your free trial, invite your team, and import existing projects — or start from one of our PMO-grade templates.", color:"#4F46E5" },
              { step:"02", icon: Target, title:"Plan and execute", desc:"Manage tasks, RAID logs, sprints, meetings, and budgets from a single connected workspace.", color:"#0EA5E9" },
              { step:"03", icon: TrendingUp, title:"Report with confidence", desc:"AI-generated status reports, automated milestone invoices, and live portfolio dashboards — always up to date.", color:"#10B981" },
            ].map((s, i) => (
              <Reveal key={s.step} delay={i * 100}>
                <div className="relative bg-white border border-gray-100 rounded-2xl p-6 hover:shadow-lg hover:shadow-gray-100 transition-all group">
                  <div className="w-11 h-11 rounded-2xl grid place-items-center mb-4 shadow-sm" style={{background:`${s.color}15`}}>
                    <s.icon className="w-5 h-5" style={{color:s.color}} />
                  </div>
                  <div className="absolute top-5 right-5 text-5xl font-bold text-gray-50 font-mono select-none">{s.step}</div>
                  <h3 className="font-semibold text-gray-800 text-base mb-2">{s.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
                  {i < 2 && <ChevronRight className="hidden lg:block absolute -right-5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-200 z-10" />}
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="mb-16">
            <div className="text-center mb-10">
              <h3 className="font-display font-bold text-xl lg:text-2xl text-gray-900 mb-2">
                ...and everything else your business runs on
              </h3>
              <p className="text-gray-500 text-sm max-w-2xl mx-auto">
                From first quote to final invoice, CombinedWork covers the full delivery lifecycle — not just the three steps above.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-8 max-w-4xl mx-auto">
              {[
                { group: "Finance & billing", items: ["Automated milestone invoicing", "Purchase orders & bills", "Retainers", "Company budgets", "Quotes & SOWs"] },
                { group: "Sales & clients", items: ["CRM deal pipeline", "Client portal & sign-off", "Client comms hub", "Marketplace"] },
                { group: "Delivery", items: ["Agile boards & sprints", "Gantt & dependencies", "RAID logs & change requests", "Task bundles & retros", "Project wiki & docs"] },
                { group: "Resourcing", items: ["Capacity planning", "Time tracking & timesheets", "Time off management"] },
                { group: "Governance & security", items: ["Compliance document vault", "SSO & SCIM provisioning", "Custom fields", "Automations"] },
                { group: "Insights & ecosystem", items: ["Executive dashboards", "AI margin-fade forecasting", "Portfolio intelligence", "In-app notifications", "50+ integrations"] },
              ].map((g) => (
                <div key={g.group}>
                  <div className="text-xs font-semibold text-violet-600 uppercase tracking-wide mb-3">{g.group}</div>
                  <ul className="space-y-2">
                    {g.items.map((item) => (
                      <li key={item} className="flex items-start gap-2 text-sm text-gray-600">
                        <CheckCircle2 className="w-4 h-4 text-violet-400 mt-0.5 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal className="text-center">
            <Link to="/pricing"
              className="inline-flex items-center gap-2 px-6 py-3 border-2 border-violet-200 text-violet-600 text-sm font-semibold rounded-full hover:bg-violet-50 transition-all">
              Get started in 2 minutes <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ── TESTIMONIALS ─────────────────────────────────────────────────── */}
      <section id="testimonials" className="py-24 px-6 bg-gradient-to-b from-violet-50 to-white">
        <div className="max-w-5xl mx-auto">
          <Reveal className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 bg-white border border-violet-100 text-violet-600 text-xs font-semibold rounded-full mb-4">What teams say</span>
            <h2 className="font-display font-bold text-3xl lg:text-4xl tracking-tight text-gray-900 mb-4">
              Built for people who deliver
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={i * 80}>
                <div className="bg-white rounded-2xl p-6 hover:shadow-lg hover:shadow-gray-100 transition-all border border-gray-100 flex flex-col gap-4">
                  <div className="flex gap-0.5">
                    {Array.from({length: t.stars}).map((_, j) => (
                      <Star key={j} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed flex-1">"{t.text}"</p>
                  <div>
                    <div className="text-gray-900 text-xs font-semibold">{t.name}</div>
                    <div className="text-gray-400 text-xs">{t.role}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="text-center">
            <Link to="/pricing"
              className="inline-flex items-center gap-2 px-6 py-3 bg-violet-600 text-white text-sm font-bold rounded-full hover:bg-violet-700 transition-all hover:scale-105 shadow-md shadow-violet-200">
              Join them — free for 7 days <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ── COMPARISON ───────────────────────────────────────────────────── */}
      <section id="comparison" className="py-24 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <Reveal className="text-center mb-14">
            <span className="inline-block px-4 py-1.5 bg-violet-100 text-violet-600 text-xs font-semibold rounded-full mb-4">Why CombinedWork</span>
            <h2 className="font-display font-bold text-3xl lg:text-4xl tracking-tight text-gray-900 mb-4">
              Less chasing. More delivering.
            </h2>
          </Reveal>

          <Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-12">
              <div className="rounded-2xl border border-gray-100 bg-gray-50 p-6 sm:p-8">
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-5">Without CombinedWork</div>
                <ul className="space-y-4">
                  {WITHOUT_WITH.map((row) => (
                    <li key={row.without} className="flex items-start gap-3">
                      <XCircle className="w-4 h-4 text-gray-300 mt-0.5 shrink-0" />
                      <span className="text-sm text-gray-500">{row.without}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-2xl border border-violet-200 bg-violet-50 p-6 sm:p-8">
                <div className="text-xs font-semibold text-violet-500 uppercase tracking-wide mb-5">With CombinedWork</div>
                <ul className="space-y-4">
                  {WITHOUT_WITH.map((row) => (
                    <li key={row.withIt} className="flex items-start gap-3">
                      <CheckCircle2 className="w-4 h-4 text-violet-500 mt-0.5 shrink-0" />
                      <span className="text-sm text-gray-800 font-medium">{row.withIt}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="text-center">
              <Link to="/pricing"
                className="inline-flex items-center gap-2 px-6 py-3 bg-violet-600 text-white text-sm font-bold rounded-full hover:bg-violet-700 transition-all hover:scale-105 shadow-md shadow-violet-200">
                Switch to CombinedWork — free for 7 days <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── AUDIENCE / WHO IT'S FOR ──────────────────────────────────────── */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-3xl mx-auto text-center">
          <Reveal>
            <h2 className="font-display font-bold text-3xl lg:text-4xl tracking-tight text-gray-900 mb-6">
              Built for teams that have outgrown spreadsheets — but don't need more complexity.
            </h2>
            <p className="text-gray-500 text-base max-w-2xl mx-auto mb-10">
              Whether you're managing a handful of projects or overseeing delivery across an organisation, CombinedWork helps create the structure your team needs without turning project management into another project.
            </p>
            <p className="font-display font-semibold text-lg sm:text-xl text-gray-800 leading-relaxed mb-2">
              Project management. Governance. Stakeholders. Resources. Risks. Reporting.
            </p>
            <p className="font-display font-bold text-lg sm:text-xl text-violet-600 mb-10">
              All connected. All in CombinedWork.
            </p>
            <Link to="/pricing"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-violet-600 text-white text-base font-bold rounded-full hover:bg-violet-700 transition-all hover:scale-105 shadow-lg shadow-violet-200">
              Start using CombinedWork <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ── PRICING ──────────────────────────────────────────────────────── */}
      <section id="pricing" className="py-24 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <Reveal className="text-center mb-8">
            <span className="inline-block px-4 py-1.5 bg-violet-100 text-violet-600 text-xs font-semibold rounded-full mb-4">Pricing</span>
            <h2 className="font-display font-bold text-3xl lg:text-4xl tracking-tight text-gray-900 mb-4">
              Simple, transparent pricing
            </h2>
            <p className="text-gray-500 text-base max-w-xl mx-auto mb-6">
              Replace Jira, Notion, Slack, and HubSpot with one subscription.
            </p>
            <div className="inline-flex items-center gap-1 bg-white border border-gray-200 rounded-full p-1 shadow-sm">
              <button onClick={() => setBillingAnnual(false)}
                className={`px-5 py-1.5 rounded-full text-xs font-semibold transition-all ${!billingAnnual ? "bg-violet-600 text-white shadow-sm" : "text-gray-400 hover:text-gray-700"}`}>
                Monthly
              </button>
              <button onClick={() => setBillingAnnual(true)}
                className={`px-5 py-1.5 rounded-full text-xs font-semibold transition-all flex items-center gap-1.5 ${billingAnnual ? "bg-violet-600 text-white shadow-sm" : "text-gray-400 hover:text-gray-700"}`}>
                Annual <span className="text-[9px] font-bold" style={{color: billingAnnual ? "#A5F3FC" : "#4F46E5"}}>SAVE 17%</span>
              </button>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
            {PLANS.map((plan, i) => (
              <Reveal key={plan.key} delay={i * 60}>
                <div className={`relative flex flex-col h-full rounded-2xl border p-6 transition-all hover:-translate-y-1 ${
                  plan.highlight
                    ? "bg-violet-600 border-violet-600 shadow-xl shadow-violet-200"
                    : "bg-white border-gray-100 hover:shadow-lg hover:shadow-gray-100"
                }`}>
                  {plan.badge && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="px-3 py-1 bg-amber-400 text-amber-900 text-xs font-bold rounded-full shadow-sm">{plan.badge}</span>
                    </div>
                  )}
                  <div className="mb-4">
                    <div className={`text-sm font-bold mb-1 ${plan.highlight ? "text-white" : "text-gray-800"}`}>{plan.label}</div>
                    <div className={`text-xs ${plan.highlight ? "text-violet-200" : "text-gray-400"}`}>{plan.limits}</div>
                  </div>
                  <div className="mb-5">
                    <span className={`font-display font-bold text-4xl tracking-tight ${plan.highlight ? "text-white" : "text-gray-900"}`}>
                      {billingAnnual ? annualPrice(plan.price) : plan.price}
                    </span>
                    {plan.price !== "Custom" && plan.price !== "Free" && <span className={`text-sm ml-1 ${plan.highlight ? "text-violet-200" : "text-gray-400"}`}>/mo</span>}
                    <div className={`text-xs mt-0.5 ${plan.highlight ? "text-violet-200" : "text-gray-400"}`}>{billingAnnual && plan.price !== "Custom" && plan.price !== "Free" ? "billed annually" : plan.sub}</div>
                  </div>
                  <ul className="flex-1 space-y-2.5 mb-6">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5">
                        <CheckCircle2 className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${plan.highlight ? "text-violet-200" : "text-violet-400"}`} />
                        <span className={`text-xs leading-relaxed ${plan.highlight ? "text-violet-100" : "text-gray-600"}`}>{f}</span>
                      </li>
                    ))}
                  </ul>
                  {plan.key === "enterprise" ? (
                    <button onClick={() => setEnterpriseOpen(true)}
                      className="block w-full text-center text-sm font-semibold py-2.5 rounded-full border-2 border-gray-200 text-gray-600 hover:border-violet-300 hover:text-violet-600 transition-colors">
                      {plan.cta}
                    </button>
                  ) : (
                    <Link to={plan.href}
                      className={`block text-center text-sm font-semibold py-2.5 rounded-full transition-colors ${
                        plan.highlight
                          ? "bg-white text-violet-600 hover:bg-violet-50 font-bold"
                          : "border-2 border-gray-200 text-gray-600 hover:border-violet-300 hover:text-violet-600"
                      }`}>
                      {plan.cta}
                    </Link>
                  )}
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-2">
              {["Annual billing saves 2 months","Cancel any time","VAT may apply"].map(t => (
                <span key={t} className="flex items-center gap-1.5 text-xs text-gray-400">
                  <span className="text-violet-500">✓</span> {t}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <section id="faq" className="py-24 px-6 bg-white">
        <div className="max-w-3xl mx-auto">
          <Reveal className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 bg-violet-100 text-violet-600 text-xs font-semibold rounded-full mb-4">FAQ</span>
            <h2 className="font-display font-bold text-3xl lg:text-4xl tracking-tight text-gray-900 mb-4">
              Questions? We've got answers.
            </h2>
          </Reveal>
          <Reveal className="space-y-3">
            {FAQS.map((f, i) => (
              <FaqItem key={f.q} q={f.q} a={f.a} isOpen={openFaq === i}
                onToggle={() => setOpenFaq((v) => (v === i ? -1 : i))} />
            ))}
          </Reveal>
        </div>
      </section>

      {/* ── BOTTOM CTA ───────────────────────────────────────────────────── */}
      <section className="relative py-28 px-6 overflow-hidden bg-obsidian">
        <div className="absolute top-0 right-0 w-96 h-96 bg-violet-600/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-violet-600/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3 pointer-events-none" />
        <div className="relative max-w-3xl mx-auto text-center">
          <Reveal>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600/20 border border-violet-500/30 rounded-full text-xs font-semibold text-violet-300 mb-6">
              <Zap className="w-3 h-3" /> 7-day free trial
            </div>
            <h2 className="font-display font-bold text-3xl lg:text-5xl tracking-tight text-white mb-6">
              See what your team<br />has been missing.
            </h2>
            <p className="text-gray-400 text-lg mb-2 max-w-xl mx-auto">
              You don't need another tool that gives you more admin.
            </p>
            <p className="text-gray-400 text-lg mb-10 max-w-xl mx-auto">
              You need one that gives you clarity. Bring your project delivery together today.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-4">
              <Link to="/pricing" data-testid="footer-cta"
                className="px-8 py-4 bg-violet-600 text-white text-base font-bold rounded-full hover:bg-violet-700 transition-all hover:scale-105 flex items-center gap-2 shadow-xl shadow-violet-900/40">
                Start for free <ArrowRight className="w-4 h-4" />
              </Link>
              <button onClick={() => setDemoOpen(true)}
                className="px-8 py-4 border-2 border-white/20 text-white text-base font-semibold rounded-full hover:border-white/50 hover:bg-white/5 transition-colors flex items-center gap-2">
                <Calendar className="w-4 h-4" /> Book a demo
              </button>
            </div>
            <p className="text-center text-xs text-gray-500 mb-6">
              No credit card required.* <Link to="/pricing" className="text-violet-300 hover:text-violet-200 underline underline-offset-2">With the project calculator</Link>
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-xs text-gray-500">
              {["7-day free trial","2-minute setup","13+ integrations","Cancel any time"].map(t => (
                <span key={t} className="flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5 text-violet-400" /> {t}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── FOOTER ───────────────────────────────────────────────────────── */}
      <footer className="px-6 pt-20 pb-10 bg-obsidian text-gray-300">
        <div className="max-w-6xl mx-auto grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-8 gap-y-12">
          {[
            {
              heading: "Product",
              links: [
                ["Integrations & apps", "#how"],
                ["Templates", "#features"],
                ["Marketplace", "#how"],
                ["Pricing", "/pricing"],
              ],
            },
            {
              heading: "Features",
              links: [
                ["Project Management", "#features"],
                ["Quoting & Budgeting", "#features"],
                ["Cost Management", "#features"],
                ["Resource Planning", "#features"],
                ["Reporting & Dashboard", "#features"],
                ["Time Tracking", "#features"],
                ["Invoicing", "#features"],
              ],
            },
            {
              heading: "Company",
              links: [
                ["Contact", "mailto:hello@combinedwork.com"],
                ["For Partners", "mailto:partners@combinedwork.com"],
                ["For Developers", "mailto:developers@combinedwork.com"],
                ["Book a demo", "#demo"],
                ["Careers", "/careers", "We're hiring!"],
              ],
            },
            {
              heading: "Resources",
              links: [
                ["Customer Success Stories", "#testimonials"],
                ["Why CombinedWork", "#comparison"],
                ["How it works", "#how"],
                ["Help Center", "/help"],
                ["Blog", "/blog"],
              ],
            },
            {
              heading: "Support",
              links: [
                ["Terms of Use", "/terms"],
                ["Privacy Policy", "/privacy"],
                ["Data Processing Agreement", "/dpa"],
                ["Security", "mailto:security@combinedwork.com"],
                ["Sign in", "/auth"],
                ["Start free trial", "/pricing"],
              ],
            },
          ].map(({ heading, links }) => (
            <div key={heading}>
              <div className="font-display text-lg font-bold text-white mb-5">{heading}</div>
              <div className="space-y-3.5">
                {links.map(([label, href, badge]) => {
                  const content = (
                    <span className="inline-flex items-center gap-2">
                      {label}
                      {badge && (
                        <span className="relative inline-flex items-center px-2 py-0.5 bg-rose-500 text-white text-[10px] font-bold rounded-md">
                          <span className="absolute -left-1 w-2 h-2 bg-rose-500 rotate-45" />
                          {badge}
                        </span>
                      )}
                    </span>
                  );
                  const className = "block text-sm text-gray-400 hover:text-white transition-colors";
                  if (href === "#demo") {
                    return (
                      <button key={label} onClick={() => setDemoOpen(true)} className={`${className} text-left`}>
                        {content}
                      </button>
                    );
                  }
                  if (href.startsWith("/")) {
                    return <Link key={label} to={href} className={className}>{content}</Link>;
                  }
                  return <a key={label} href={href} className={className}>{content}</a>;
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="max-w-6xl mx-auto mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-xl bg-violet-600 grid place-items-center shadow-md shadow-violet-900/40">
              <LogoMark size={14} color="#fff" />
            </div>
            <span className="text-sm font-bold text-white">Combined<span className="text-violet-400">Work</span></span>
          </Link>
          <p className="text-xs text-gray-500">© {new Date().getFullYear()} CombinedWork. All rights reserved.</p>
        </div>
      </footer>

      {demoOpen && <BookDemoModal onClose={() => setDemoOpen(false)} />}
      {enterpriseOpen && <EnterpriseModal onClose={() => setEnterpriseOpen(false)} />}
    </div>
  );
};

export default Landing;
